import { clearDemoSession, isDemoActive } from './demoSession';
import { demoHandleFetch, demoHandleUpload } from './demoRouter';

const DEFAULT_BASE = '/api';

const MEDIA_LIST_TTL_MS = 60_000;
const mediaListCache = new Map();

function mediaCacheKey(siteId) {
  return String(Number(siteId));
}

function mediaCacheGet(siteId) {
  const key = mediaCacheKey(siteId);
  const hit = mediaListCache.get(key);
  if (!hit) return null;
  if (!hit.ts || Date.now() - hit.ts > MEDIA_LIST_TTL_MS) {
    mediaListCache.delete(key);
    return null;
  }
  return hit.assets || null;
}

function mediaCacheSet(siteId, assets) {
  const key = mediaCacheKey(siteId);
  mediaListCache.set(key, { ts: Date.now(), assets: assets || [] });
}

function mediaCacheInvalidate(siteId) {
  mediaListCache.delete(mediaCacheKey(siteId));
}

function getBaseUrl() {
  const raw = process.env.REACT_APP_API_BASE_URL;
  if (raw && raw.trim()) return raw.replace(/\/+$/, '');
  return DEFAULT_BASE;
}

function demoBypass(path) {
  const raw = String(path || '');
  try {
    const u = new URL(raw, 'http://local');
    return String(u.pathname || '').endsWith('/demo.php');
  } catch {
    return false;
  }
}

async function apiFetch(path, { method = 'GET', body, headers = {} } = {}) {
  if (typeof window !== 'undefined' && isDemoActive() && !demoBypass(path)) {
    return demoHandleFetch(path, { method, body, headers });
  }

  const base = getBaseUrl();
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;

  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', ...headers },
    credentials: 'include',
    body: body == null ? undefined : JSON.stringify(body),
  });

  let data = null;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    data = await res.json().catch(() => null);
  }

  if (!res.ok) {
    if (res.status === 401 && typeof window !== 'undefined') {
      try {
        window.dispatchEvent(
          new CustomEvent('omniscope:unauthorized', {
            detail: { path: String(path || ''), status: res.status, error: data?.error || 'Non authentifié' },
          })
        );
      } catch {
        // ignore
      }
    }
    return { ok: false, status: res.status, error: data?.error || 'Erreur API', ...(data && typeof data === 'object' ? data : {}) };
  }

  return { ok: true, status: res.status, ...data };
}

async function apiUpload(path, formData, { method = 'POST', headers = {} } = {}) {
  if (typeof window !== 'undefined' && isDemoActive() && !demoBypass(path)) {
    return demoHandleUpload(path, { method, formData, headers });
  }

  const base = getBaseUrl();
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  const res = await fetch(url, {
    method,
    headers: { ...headers },
    credentials: 'include',
    body: formData,
  });

  let data = null;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    data = await res.json().catch(() => null);
  }
  if (!res.ok) {
    return { ok: false, status: res.status, error: data?.error || 'Erreur API', ...(data && typeof data === 'object' ? data : {}) };
  }
  return { ok: true, status: res.status, ...data };
}

export const api = {
  me() {
    return apiFetch('/me.php', { method: 'GET' });
  },
  sessionStatus() {
    return apiFetch('/session_status.php', { method: 'GET' });
  },
  login({ email, password }) {
    return apiFetch('/login.php', { method: 'POST', body: { email, password } });
  },
  logout() {
    if (typeof window !== 'undefined' && isDemoActive()) {
      clearDemoSession();
      return Promise.resolve({ ok: true, status: 200 });
    }
    return apiFetch('/logout.php', { method: 'POST', body: {} });
  },
  accountPasswordUpdate({ current_password, new_password } = {}) {
    return apiFetch('/account_password.php', { method: 'POST', body: { current_password, new_password } });
  },
  demoInfo(token) {
    const t = String(token || '').trim();
    return apiFetch(`/demo.php?token=${encodeURIComponent(t)}`, { method: 'GET' });
  },
  adminClientDemo(clientId) {
    const id = Number(clientId);
    return apiFetch(`/demo.php?client_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  adminClientDemoUpsert({ client_id, is_enabled, label, module_keys } = {}) {
    return apiFetch('/demo.php', {
      method: 'PUT',
      body: { action: 'upsert', client_id, is_enabled, label, module_keys },
    });
  },
  sites() {
    return apiFetch('/sites.php', { method: 'GET' });
  },
  siteLanguages(siteId) {
    const id = Number(siteId);
    return apiFetch(`/site_languages.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  siteLanguagesUpdate(payload) {
    return apiFetch('/site_languages.php', { method: 'PUT', body: payload });
  },
  modules(siteId, { as } = {}) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    if (as) qs.set('as', as);
    return apiFetch(`/modules.php?${qs.toString()}`, { method: 'GET' });
  },
  panelSettings(siteId, { client_id } = {}) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    if (client_id != null && client_id !== '') qs.set('client_id', String(Number(client_id)));
    return apiFetch(`/panel_settings.php?${qs.toString()}`, { method: 'GET' });
  },
  panelSettingsUpdate({ site_id, client_id, settings, force_reconnect } = {}) {
    const payload = { site_id, settings };
    if (client_id != null && client_id !== '') payload.client_id = Number(client_id);
    if (force_reconnect) payload.force_reconnect = true;
    return apiFetch('/panel_settings.php', { method: 'PUT', body: payload });
  },
  members(siteId) {
    const id = Number(siteId);
    return apiFetch(`/members.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  createMember({ site_id, email, display_name, member_role, modules }) {
    return apiFetch('/members.php', {
      method: 'POST',
      body: { site_id, email, display_name, member_role, modules },
    });
  },
  updateRolePermissions({ site_id, member_role, modules }) {
    return apiFetch('/members.php', {
      method: 'PUT',
      body: { action: 'update_role', site_id, member_role, modules },
    });
  },
  updateMembership({ site_id, membership_id, member_role, modules }) {
    return apiFetch('/members.php', {
      method: 'PUT',
      body: { action: 'update_membership', site_id, membership_id, member_role, modules },
    });
  },
  adminClients() {
    return apiFetch('/admin_clients.php', { method: 'GET' });
  },
  adminClientCreate(client) {
    return apiFetch('/admin_clients.php', { method: 'POST', body: client });
  },
  adminClientUpdate(client) {
    return apiFetch('/admin_clients.php', { method: 'PUT', body: client });
  },
  adminClientDelete({ id, keep_sites }) {
    return apiFetch('/admin_clients.php', { method: 'DELETE', body: { id, keep_sites } });
  },
  adminEmployees() {
    return apiFetch('/admin_employees.php', { method: 'GET' });
  },
  adminEmployeeCreate(employee) {
    return apiFetch('/admin_employees.php', { method: 'POST', body: employee });
  },
  adminEmployeeUpdate(employee) {
    return apiFetch('/admin_employees.php', { method: 'PUT', body: employee });
  },
  adminEmployeeDisable({ id }) {
    return apiFetch('/admin_employees.php', { method: 'DELETE', body: { id } });
  },
  adminLogs({ site_id, module_key, user_id, q, limit, before_id } = {}) {
    const qs = new URLSearchParams();
    if (site_id != null && site_id !== '' && Number(site_id) > 0) qs.set('site_id', String(Number(site_id)));
    if (module_key) qs.set('module_key', String(module_key));
    if (user_id != null && user_id !== '' && Number(user_id) > 0) qs.set('user_id', String(Number(user_id)));
    if (q) qs.set('q', String(q));
    if (limit != null) qs.set('limit', String(Number(limit)));
    if (before_id != null && before_id !== '' && Number(before_id) > 0) qs.set('before_id', String(Number(before_id)));
    qs.set('_ts', String(Date.now()));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiFetch(`/admin_logs.php${suffix}`, { method: 'GET' });
  },
  adminSites({ client_id } = {}) {
    const qs = client_id ? `?client_id=${encodeURIComponent(String(client_id))}` : '';
    return apiFetch(`/admin_sites.php${qs}`, { method: 'GET' });
  },
  adminSiteCreate(site) {
    return apiFetch('/admin_sites.php', { method: 'POST', body: site });
  },
  adminSiteUpdate(site) {
    return apiFetch('/admin_sites.php', { method: 'PUT', body: site });
  },
  adminSiteDelete({ id }) {
    return apiFetch('/admin_sites.php', { method: 'DELETE', body: { id } });
  },
  adminSiteModules(siteId) {
    const id = Number(siteId);
    return apiFetch(`/admin_site_modules.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  adminSiteModulesUpdate({ site_id, enabled_keys }) {
    return apiFetch('/admin_site_modules.php', { method: 'PUT', body: { site_id, enabled_keys } });
  },
  adminClientAccess(clientId) {
    const id = Number(clientId);
    return apiFetch(`/admin_client_access.php?client_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  adminClientAccessUpsert(payload) {
    return apiFetch('/admin_client_access.php', { method: 'PUT', body: { action: 'upsert', ...payload } });
  },
  adminClientAccessInvalidate({ client_id }) {
    return apiFetch('/admin_client_access.php', { method: 'PUT', body: { action: 'invalidate', client_id } });
  },
  appointmentsList(siteId, { limit } = {}) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    if (limit != null) qs.set('limit', String(Number(limit)));
    return apiFetch(`/appointments.php?${qs.toString()}`, { method: 'GET' });
  },
  appointmentsCreate(payload) {
    return apiFetch('/appointments.php', { method: 'POST', body: payload });
  },
  appointmentsUpdate(payload) {
    return apiFetch('/appointments.php', { method: 'PUT', body: payload });
  },
  appointmentsDelete(payload) {
    return apiFetch('/appointments.php', { method: 'DELETE', body: payload });
  },
  updatesList(siteId, { limit } = {}) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    if (limit != null) qs.set('limit', String(Number(limit)));
    return apiFetch(`/updates.php?${qs.toString()}`, { method: 'GET' });
  },
  updatesCreate(payload) {
    return apiFetch('/updates.php', { method: 'POST', body: payload });
  },
  updatesUpdate(payload) {
    return apiFetch('/updates.php', { method: 'PUT', body: payload });
  },
  updatesDelete(payload) {
    return apiFetch('/updates.php', { method: 'DELETE', body: payload });
  },
  menusGet(siteId) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    return apiFetch(`/menus.php?${qs.toString()}`, { method: 'GET' });
  },
  menusEnsure(siteId) {
    return apiFetch('/menus_ensure.php', { method: 'POST', body: { site_id: Number(siteId) } });
  },
  menusDbTest(payload) {
    return apiFetch('/menus_db_test.php', { method: 'POST', body: payload });
  },
  menusSave(payload) {
    return apiFetch('/menus.php', { method: 'PUT', body: payload });
  },
  scriptsList(siteId, { limit, include_runs, run_limit } = {}) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    if (limit != null) qs.set('limit', String(Number(limit)));
    if (include_runs) qs.set('include_runs', '1');
    if (run_limit != null) qs.set('run_limit', String(Number(run_limit)));
    return apiFetch(`/scripts.php?${qs.toString()}`, { method: 'GET' });
  },
  scriptsCreate(payload) {
    return apiFetch('/scripts.php', { method: 'POST', body: payload });
  },
  scriptsUpdate(payload) {
    return apiFetch('/scripts.php', { method: 'PUT', body: payload });
  },
  scriptsDelete(payload) {
    return apiFetch('/scripts.php', { method: 'DELETE', body: payload });
  },
  scriptsRun(payload) {
    return apiFetch('/scripts_run.php', { method: 'POST', body: payload });
  },
  scriptsCompile(payload) {
    return apiFetch('/scripts_compile.php', { method: 'POST', body: payload });
  },
  scriptsRunsClear(payload) {
    return apiFetch('/scripts_run.php', { method: 'DELETE', body: payload });
  },
  traffic(siteId, { range, target, page_hash } = {}) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    if (range) qs.set('range', String(range));
    if (target) qs.set('target', String(target));
    if (page_hash) qs.set('page_hash', String(page_hash));
    return apiFetch(`/traffic.php?${qs.toString()}`, { method: 'GET' });
  },
  trafficHealth(siteId) {
    const id = Number(siteId);
    return apiFetch(`/traffic_health.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  trafficCollectDryRun({ tracking_id } = {}) {
    const payload = {
      tracking_id: String(tracking_id || '').trim(),
      events: [{ type: 'pageview', ts_ms: Date.now(), path: '/__dry_run__' }],
    };
    return apiFetch('/px.php?dry_run=1', { method: 'POST', body: payload });
  },
  trafficPlanUpdate(payload) {
    return apiFetch('/traffic.php', { method: 'PUT', body: payload });
  },
  async trafficDetectPages({ site_id, max_pages, project_template_name, article_template_name } = {}) {
    const payload = {
      action: 'detect_pages',
      site_id: Number(site_id),
      max_pages: max_pages == null ? 60 : Number(max_pages),
    };
    if (project_template_name != null) payload.project_template_name = String(project_template_name);
    if (article_template_name != null) payload.article_template_name = String(article_template_name);
    const first = await apiFetch('/traffic.php', {
      method: 'POST',
      body: payload,
    });
    if (first?.ok || Number(first?.status) !== 405) return first;
    return apiFetch('/traffic.php', {
      method: 'PUT',
      body: payload,
    });
  },
  appointmentPublicGet(token) {
    const t = String(token || '').trim();
    return apiFetch(`/appointment_public.php?token=${encodeURIComponent(t)}`, { method: 'GET' });
  },
  appointmentPublicAction({ token, action, date, time } = {}) {
    const payload = { token, action };
    if (date != null) payload.date = date;
    if (time != null) payload.time = time;
    return apiFetch('/appointment_public.php', { method: 'POST', body: payload });
  },
  projectsDb(siteId) {
    const id = Number(siteId);
    return apiFetch(`/projects_db.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  projectsDbSave(payload) {
    return apiFetch('/projects_db.php', { method: 'POST', body: payload });
  },
  projectsDbTest(payload) {
    return apiFetch('/projects_db_test.php', { method: 'POST', body: payload });
  },
  dbConnections(siteId) {
    const id = Number(siteId);
    return apiFetch(`/databases.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  dbConnectionSave(payload) {
    return apiFetch('/databases.php', { method: 'POST', body: payload });
  },
  dbConnectionDelete(payload) {
    return apiFetch('/databases.php', { method: 'DELETE', body: payload });
  },
  dbConnectionTest(payload) {
    return apiFetch('/databases_test.php', { method: 'POST', body: payload });
  },
  async mediaList(siteId) {
    const id = Number(siteId);
    const cached = mediaCacheGet(id);
    if (cached) return { ok: true, status: 200, assets: cached, cached: true };
    const res = await apiFetch(`/media.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
    if (res.ok) mediaCacheSet(id, res.assets || []);
    return res;
  },
  async mediaUpload(siteId, files, { project_id, folder_id } = {}) {
    const id = Number(siteId);
    const fd = new FormData();
    fd.set('site_id', String(id));
    if (project_id != null && project_id !== '') fd.set('project_id', String(project_id));
    if (folder_id != null && folder_id !== '') fd.set('folder_id', String(folder_id));
    (files || []).forEach((f) => fd.append('files[]', f));
    const res = await apiUpload('/media.php', fd, { method: 'POST' });
    if (res.ok && res.assets) mediaCacheSet(id, res.assets);
    else mediaCacheInvalidate(id);
    return res;
  },
  mediaListScoped(siteId, { folder_id, link_type, link_id, project_id, unassigned, limit } = {}) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    if (limit != null) qs.set('limit', String(Number(limit)));
    if (folder_id !== undefined) qs.set('folder_id', folder_id == null || folder_id === '' ? '0' : String(Number(folder_id)));
    if (unassigned) qs.set('unassigned', '1');
    if (link_type) qs.set('link_type', String(link_type));
    if (link_id != null) qs.set('link_id', String(Number(link_id)));
    if (project_id != null) qs.set('project_id', String(Number(project_id)));
    return apiFetch(`/media.php?${qs.toString()}`, { method: 'GET' });
  },
  mediaFolders(siteId, { parent_id, all } = {}) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    if (all) qs.set('all', '1');
    if (parent_id !== undefined) qs.set('parent_id', parent_id == null || parent_id === '' ? '' : String(Number(parent_id)));
    return apiFetch(`/media_folders.php?${qs.toString()}`, { method: 'GET' });
  },
  mediaFolderCreate(payload) {
    return apiFetch('/media_folders.php', { method: 'POST', body: payload });
  },
  mediaFolderRename(payload) {
    return apiFetch('/media_folders.php', { method: 'PUT', body: payload });
  },
  mediaFolderDelete(payload) {
    return apiFetch('/media_folders.php', { method: 'DELETE', body: payload });
  },
  async mediaUpdate({ site_id, id, note, original_name, is_hidden }) {
    const payload = { site_id, id };
    if (note != null) payload.note = note;
    if (original_name != null) payload.original_name = original_name;
    if (is_hidden != null) payload.is_hidden = is_hidden;
    const res = await apiFetch('/media.php', { method: 'PUT', body: payload });
    if (res.ok && res.assets) mediaCacheSet(site_id, res.assets);
    else mediaCacheInvalidate(site_id);
    return res;
  },
  async mediaDelete({ site_id, id }) {
    const res = await apiFetch('/media.php', { method: 'DELETE', body: { site_id, id } });
    if (res.ok && res.assets) mediaCacheSet(site_id, res.assets);
    else mediaCacheInvalidate(site_id);
    return res;
  },
  async mediaToWebp({ site_id, id }) {
    const res = await apiFetch('/media.php', { method: 'POST', body: { site_id, action: 'to_webp', id } });
    if (res.ok && res.assets) mediaCacheSet(site_id, res.assets);
    else mediaCacheInvalidate(site_id);
    return res;
  },
  async mediaMoveToFolder({ site_id, ids, folder_id } = {}) {
    const payload = { site_id, action: 'move_to_folder', ids };
    if (folder_id != null) payload.folder_id = folder_id;
    const res = await apiFetch('/media.php', { method: 'POST', body: payload });
    if (res.ok && res.assets) mediaCacheSet(site_id, res.assets);
    else mediaCacheInvalidate(site_id);
    return res;
  },
  async mediaSetLinks({ site_id, id, links }) {
    const res = await apiFetch('/media.php', { method: 'POST', body: { site_id, action: 'set_links', id, links } });
    if (res.ok && res.assets) mediaCacheSet(site_id, res.assets);
    else mediaCacheInvalidate(site_id);
    return res;
  },
  async mediaScan({ site_id, id }) {
    const res = await apiFetch('/media.php', { method: 'POST', body: { site_id, action: 'scan', id } });
    if (res.ok && res.assets) mediaCacheSet(site_id, res.assets);
    else mediaCacheInvalidate(site_id);
    return res;
  },
  async mediaTogglePin({ site_id, id, link_type, link_id, is_pinned }) {
    const res = await apiFetch('/media.php', { method: 'POST', body: { site_id, action: 'toggle_pin', id, link_type, link_id, is_pinned } });
    if (res.ok && res.assets) mediaCacheSet(site_id, res.assets);
    else mediaCacheInvalidate(site_id);
    return res;
  },
  galleryEnsure(siteId) {
    return apiFetch('/gallery_ensure.php', { method: 'POST', body: { site_id: Number(siteId) } });
  },
  galleryDbTest(payload) {
    return apiFetch('/gallery_db_test.php', { method: 'POST', body: payload });
  },
  galleryList(siteId, { category_id, limit } = {}) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    if (category_id != null && category_id !== '') qs.set('category_id', String(Number(category_id)));
    if (limit != null) qs.set('limit', String(Number(limit)));
    return apiFetch(`/gallery.php?${qs.toString()}`, { method: 'GET' });
  },
  galleryAddMedia({ site_id, media_ids, category_id } = {}) {
    return apiFetch('/gallery.php', { method: 'POST', body: { site_id, action: 'add_media', media_ids, category_id } });
  },
  galleryRemoveMedia({ site_id, media_ids } = {}) {
    return apiFetch('/gallery.php', { method: 'POST', body: { site_id, action: 'remove_media', media_ids } });
  },
  galleryItemUpdate(payload) {
    return apiFetch('/gallery.php', { method: 'PUT', body: payload });
  },
  galleryItemDelete(payload) {
    return apiFetch('/gallery.php', { method: 'DELETE', body: payload });
  },
  galleryCategories(siteId) {
    const id = Number(siteId);
    return apiFetch(`/gallery_categories.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  galleryCategoryCreate(payload) {
    return apiFetch('/gallery_categories.php', { method: 'POST', body: payload });
  },
  galleryCategoryUpdate(payload) {
    return apiFetch('/gallery_categories.php', { method: 'PUT', body: payload });
  },
  galleryCategoryDelete(payload) {
    return apiFetch('/gallery_categories.php', { method: 'DELETE', body: payload });
  },
  galleryTags(siteId) {
    const id = Number(siteId);
    return apiFetch(`/gallery_tags.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  galleryTagCreate(payload) {
    return apiFetch('/gallery_tags.php', { method: 'POST', body: payload });
  },
  galleryTagUpdate(payload) {
    return apiFetch('/gallery_tags.php', { method: 'PUT', body: payload });
  },
  galleryTagDelete(payload) {
    return apiFetch('/gallery_tags.php', { method: 'DELETE', body: payload });
  },
  projectsEnsure(siteId) {
    return apiFetch('/projects_ensure.php', { method: 'POST', body: { site_id: Number(siteId) } });
  },
  projectsList(siteId, { lang } = {}) {
    const id = Number(siteId);
    const qs = new URLSearchParams({ site_id: String(id) });
    if (lang) qs.set('lang', String(lang));
    return apiFetch(`/projects.php?${qs.toString()}`, { method: 'GET' });
  },
  projectsReorder(siteId, ids) {
    const id = Number(siteId);
    return apiFetch('/projects.php', {
      method: 'PUT',
      body: { site_id: id, action: 'reorder', ids: Array.isArray(ids) ? ids : [] },
    });
  },
  projectGet({ site_id, id, lang }) {
    const qs = new URLSearchParams({ site_id: String(Number(site_id)), id: String(Number(id)) });
    if (lang) qs.set('lang', String(lang));
    return apiFetch(`/projects.php?${qs.toString()}`, {
      method: 'GET',
    });
  },
  projectCreate(payload) {
    return apiFetch('/projects.php', { method: 'POST', body: payload });
  },
  projectUpdate(payload) {
    return apiFetch('/projects.php', { method: 'PUT', body: payload });
  },
  projectDelete(payload) {
    return apiFetch('/projects.php', { method: 'DELETE', body: payload });
  },
  projectTags(siteId) {
    const id = Number(siteId);
    return apiFetch(`/project_tags.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  projectTagCreate(payload) {
    return apiFetch('/project_tags.php', { method: 'POST', body: payload });
  },
  projectTagUpdate(payload) {
    return apiFetch('/project_tags.php', { method: 'PUT', body: payload });
  },
  projectTagDelete(payload) {
    return apiFetch('/project_tags.php', { method: 'DELETE', body: payload });
  },
  projectCategories(siteId) {
    const id = Number(siteId);
    return apiFetch(`/project_categories.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  projectCategoryCreate(payload) {
    return apiFetch('/project_categories.php', { method: 'POST', body: payload });
  },
  projectCategoryUpdate(payload) {
    return apiFetch('/project_categories.php', { method: 'PUT', body: payload });
  },
  projectCategoryDelete(payload) {
    return apiFetch('/project_categories.php', { method: 'DELETE', body: payload });
  },
  projectStyles(siteId) {
    const id = Number(siteId);
    return apiFetch(`/project_styles.php?site_id=${encodeURIComponent(String(id))}`, { method: 'GET' });
  },
  projectStyleCreate(payload) {
    return apiFetch('/project_styles.php', { method: 'POST', body: payload });
  },
  projectStyleUpdate(payload) {
    return apiFetch('/project_styles.php', { method: 'PUT', body: payload });
  },
  projectStyleDelete(payload) {
    return apiFetch('/project_styles.php', { method: 'DELETE', body: payload });
  },
};
